import { Fragment, useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SummaryMetricCard, MetricGrid, FormattedNumber } from '@/components/global';
import { TablePagination } from '@/components/global/TablePagination';
import { RAGBadge } from '@/components/global/RAGBadge';
import { ProductIdentity } from '@/components/products';
import { WarehouseTypeBadge } from '@/components/locations';
import { Search, Package, AlertTriangle, XCircle, ChevronDown, ChevronRight, ShieldAlert } from 'lucide-react';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

interface BatchPosition {
  id: string;
  productId: string;
  warehouseId: string;
  batchNumber: string;
  quantityRemaining: number;
  expiryDate: string | null;
  receivedAt: string;
  daysUntilExpiry: number | null;
  isExpiringSoon: boolean;
  isExpired: boolean;
  warehouse?: {
    id: string;
    code: string;
    name: string;
    type: 'HUB' | 'SPOKE';
  };
}

interface BatchGroup {
  groupKey: string;
  productId: string;
  batchNumber: string;
  totalQuantityRemaining: number;
  expiryDate: string | null;
  receivedAt: string;
  daysUntilExpiry: number | null;
  isExpiringSoon: boolean;
  isExpired: boolean;
  locationCount: number;
  product?: {
    id: string;
    sku: string;
    name: string;
  };
  positions: BatchPosition[];
}

interface BatchSummary {
  totalBatches: number;
  totalQuantity: number;
  expiringWithin30Days: number;
  expiredBatches: number;
}

interface PaginatedResult {
  items: BatchGroup[];
  total: number;
  page: number;
  pageSize: number;
}

function formatExpiryDisplay(group: Pick<BatchGroup, 'expiryDate' | 'daysUntilExpiry' | 'isExpired' | 'isExpiringSoon'>): {
  label: string;
  status: 'green' | 'amber' | 'red';
} {
  if (!group.expiryDate) {
    return { label: 'No expiry', status: 'green' };
  }
  if (group.isExpired) {
    return { label: 'Expired', status: 'red' };
  }
  if (group.isExpiringSoon) {
    return { label: `${group.daysUntilExpiry}d left`, status: 'amber' };
  }
  return { label: `${group.daysUntilExpiry}d left`, status: 'green' };
}

export default function BatchesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [groups, setGroups] = useState<BatchGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(50);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [warehouseFilter, setWarehouseFilter] = useState(searchParams.get('warehouseId') || 'all');
  const [expiringFilter, setExpiringFilter] = useState(searchParams.get('expiringWithinDays') || 'all');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [summary, setSummary] = useState<BatchSummary>({
    totalBatches: 0,
    totalQuantity: 0,
    expiringWithin30Days: 0,
    expiredBatches: 0,
  });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('grouped', 'true');
      if (search) params.set('search', search);
      if (warehouseFilter && warehouseFilter !== 'all') params.set('warehouseId', warehouseFilter);
      if (expiringFilter && expiringFilter !== 'all') params.set('expiringWithinDays', expiringFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/batches?${params}`);
      if (res.ok) {
        const data: PaginatedResult = await res.json();
        setGroups(data.items);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error loading batches:', error);
    } finally {
      setLoading(false);
    }
  }, [search, warehouseFilter, expiringFilter, page, pageSize]);

  const loadFilters = useCallback(async () => {
    try {
      const [warehouseRes, summaryRes] = await Promise.all([
        apiFetch('/api/v1/warehouses'),
        apiFetch('/api/v1/batches/summary'),
      ]);

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouses(data.items || []);
      }

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (warehouseFilter && warehouseFilter !== 'all') params.set('warehouseId', warehouseFilter);
    if (expiringFilter && expiringFilter !== 'all') params.set('expiringWithinDays', expiringFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  }, [search, warehouseFilter, expiringFilter, page, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function toggleExpanded(groupKey: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Batch Tracking</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Supplier batch numbers across hub, spoke, and machine locations.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/inventory/batch-recall">
              <ShieldAlert className="h-4 w-4 mr-2" />
              Batch recall trace
            </Link>
          </Button>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Supplier Batches"
            value={summary.totalBatches}
            icon={Package}
          />
          <SummaryMetricCard
            label="Total Quantity"
            value={summary.totalQuantity}
            icon={Package}
          />
          <SummaryMetricCard
            label="Expiring in 30 Days"
            value={summary.expiringWithin30Days}
            icon={AlertTriangle}
            status={summary.expiringWithin30Days > 0 ? 'amber' : 'green'}
          />
          <SummaryMetricCard
            label="Expired Batches"
            value={summary.expiredBatches}
            icon={XCircle}
            status={summary.expiredBatches > 0 ? 'red' : 'green'}
          />
        </MetricGrid>

        <Card className="mb-6">
          <div className="p-4 flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search batch # or product..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={warehouseFilter}
              onValueChange={(v) => {
                setWarehouseFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {warehouseOptionLabel(w)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={expiringFilter}
              onValueChange={(v) => {
                setExpiringFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                <SelectItem value="7">Expiring in 7 days</SelectItem>
                <SelectItem value="14">Expiring in 14 days</SelectItem>
                <SelectItem value="30">Expiring in 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead className="hidden sm:table-cell">Supplier Batch #</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Total Qty</TableHead>
                <TableHead className="hidden text-right md:table-cell">Location(s)</TableHead>
                <TableHead className="hidden lg:table-cell">First Received</TableHead>
                <TableHead>Expiry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No batches found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => {
                  const expiry = formatExpiryDisplay(group);
                  const isExpanded = expanded.has(group.groupKey);

                  return (
                    <Fragment key={group.groupKey}>
                      <TableRow>
                        <TableCell className="pr-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Hide locations' : 'Show locations'}
                            onClick={() => toggleExpanded(group.groupKey)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="hidden font-mono font-medium sm:table-cell">{group.batchNumber}</TableCell>
                        <TableCell>
                          <ProductIdentity
                            name={group.product?.name ?? '—'}
                            sku={group.product?.sku}
                            productId={group.productId}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <FormattedNumber value={group.totalQuantityRemaining} />
                        </TableCell>
                        <TableCell className="hidden text-right tabular-nums text-muted-foreground md:table-cell">
                          {group.locationCount} location{group.locationCount === 1 ? '' : 's'}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{new Date(group.receivedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {group.expiryDate ? (
                            <div className="flex items-center gap-2">
                              <span>{new Date(group.expiryDate).toLocaleDateString()}</span>
                              <RAGBadge status={expiry.status} label={expiry.label} size="sm" />
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={7} className="bg-muted/10 py-3 pl-4 pr-4 md:pl-12">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Location</TableHead>
                                  <TableHead className="text-right">Qty Remaining</TableHead>
                                  <TableHead>Received</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.positions.map((position) => (
                                  <TableRow key={position.id}>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <span>{position.warehouse?.name ?? '—'}</span>
                                        {position.warehouse?.type && (
                                          <WarehouseTypeBadge
                                            type={position.warehouse.type}
                                            size="sm"
                                            showIcon={false}
                                          />
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <FormattedNumber value={position.quantityRemaining} />
                                    </TableCell>
                                    <TableCell>
                                      {new Date(position.receivedAt).toLocaleDateString()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={total}
            itemLabel="batches"
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </Card>
      </div>
    </div>
  );
}
